/**
 * OWLY CRM - Massive Lead Generator
 * Utility for generating large datasets for performance testing
 */

import { Lead } from '../../types';

const nombres = [
  "Carlos & Ana García", "Diego & Camila Morales", "Carmen Vega", "Familia Ruiz-Silva", "Andrés & Valentina",
  "Familia Hernández", "Sofia & Chen Wang", "Familia López-Martín", "Roberto & Ana Kim", "Julián & Paola Sánchez",
  "Los Martínez", "Isabella & Miguel Torres", "Familia Jiménez", "Sandra & Luis Pérez", "Los Rodríguez-Luna",
  "Natalia & David Castro", "Familia Ortega", "Carolina & Felipe Gómez", "Los Valencia", "María & Juan Vargas",
  "Familia Restrepo", "Alejandra & Carlos Mora", "Los Aguilar", "Daniela & Sebastián Díaz", "Familia Muñoz",
  "Lucía & Pablo Herrera", "Los Delgado", "Valeria & Andrés Rojas", "Familia Ramírez", "Sofía & Mateo Cruz",
  "Los Guerrero", "Camila & Diego Medina", "Familia Vásquez", "Andrea & Nicolás Parra", "Los Silva-Moreno",
  "Gabriela & Eduardo León", "Familia Castaño", "Lorena & Ricardo Mendoza", "Los Giraldo", "Patricia & Javier Ramos",
  "Familia Cardona", "Diana & Alejandro Ruiz", "Los Ospina", "Claudia & Fernando Ríos", "Familia Montoya",
  "Tatiana & Mauricio Bedoya", "Los Franco", "Adriana & Gustavo Soto", "Familia Henao", "Marcela & Jorge Álvarez"
];

const apellidos = [
  "García", "Morales", "Vega", "Silva", "Hernández", "Wang", "López", "Kim", "Sánchez", "Martínez",
  "Torres", "Jiménez", "Pérez", "Luna", "Castro", "Ortega", "Gómez", "Valencia", "Vargas", "Restrepo",
  "Mora", "Aguilar", "Díaz", "Muñoz", "Herrera", "Delgado", "Rojas", "Ramírez", "Cruz", "Guerrero",
  "Medina", "Vásquez", "Parra", "Moreno", "León", "Castaño", "Mendoza", "Giraldo", "Ramos", "Cardona",
  "Ruiz", "Ospina", "Ríos", "Montoya", "Bedoya", "Franco", "Soto", "Henao", "Álvarez", "Rodríguez"
];

const primeros_nombres = [
  "Carlos", "Ana", "Diego", "Camila", "Carmen", "Andrés", "Valentina", "Sofia", "Roberto", "Julián",
  "Paola", "Isabella", "Miguel", "Sandra", "Luis", "Natalia", "David", "Carolina", "Felipe", "María",
  "Juan", "Alejandra", "Daniela", "Sebastián", "Lucía", "Pablo", "Valeria", "Sofía", "Mateo", "Gabriela",
  "Eduardo", "Lorena", "Ricardo", "Patricia", "Javier", "Diana", "Alejandro", "Claudia", "Fernando", "Tatiana",
  "Mauricio", "Adriana", "Gustavo", "Marcela", "Jorge", "Elena", "Felipe", "Camilo", "Mónica", "Esteban"
];

const apartamentos = [
  "Apt 1 Alcoba Torre Norte", "Apt 2 Alcobas Torre Sur", "Apt 3 Alcobas Torre Norte", "Penthouse Torre Sur",
  "Duplex Torre Norte", "Apt 1 Alcoba + Den", "Apt 2 Alcobas + Estudio", "Apt 3 Alcobas + Balcón",
  "Studio Torre Norte", "Loft Torre Sur", "Apt 2 Alcobas Vista Ciudad", "Apt 3 Alcobas Vista Montaña",
  "Apt 1 Alcoba Vista Parque", "Apt 2 Alcobas Premium", "Apt 3 Alcobas Esquinero", "Penthouse Duplex"
];

const profesiones = [
  "Ingeniero", "Médico", "Abogado", "Arquitecto", "Contador", "Profesor", "Empresario", "Desarrollador",
  "Gerente", "Consultor", "Director", "Analista", "Diseñador", "Vendedor", "Administrador", "Psicólogo",
  "Dentista", "Veterinario", "Enfermero", "Farmacéutico", "Fisioterapeuta", "Nutricionista", "Chef",
  "Periodista", "Publicista", "Artista", "Músico", "Fotógrafo", "Piloto", "Conductor"
];

const canales = [
  "Página web", "Referido", "Instagram", "Facebook Ads", "LinkedIn", "Google Ads", "Visita directa",
  "Feria inmobiliaria", "WhatsApp", "Llamada telefónica", "Email marketing", "YouTube", "TikTok",
  "Radio", "Periódico", "Valla publicitaria", "Evento corporativo", "Recomendación familiar"
];

const motivosRechazo = [
  "Presupuesto insuficiente",
  "Timing no adecuado",
  "Eligió competencia",
  "No aprobó crédito",
  "Cambió de planes",
  "Ubicación no conveniente",
  "Unidad no disponible",
  "Problemas familiares",
  "No le gustó el proyecto",
  "Precio muy alto"
];

const motivosDesistimiento = [
  "No aprobó crédito hipotecario",
  "Problemas financieros personales",
  "Cambió de ciudad por trabajo",
  "Situación familiar inesperada",
  "Encontró mejor opción",
  "No cumplió con documentación",
  "Retraso en desembolso de crédito",
  "Cambió de decisión",
  "Problemas de salud",
  "Crisis económica personal"
];

const bancos = [
  "Bancolombia",
  "Banco de Bogotá",
  "Davivienda",
  "BBVA Colombia",
  "Banco de Occidente",
  "Banco Popular",
  "Scotiabank Colpatria",
  "Itaú",
  "Banco AV Villas",
  "Banco Caja Social"
];

/**
 * Genera un nombre aleatorio más variado
 */
function generateRandomName(index: number): string {
  if (index < nombres.length) {
    return nombres[index];
  }

  // Para más de 50, generar combinaciones
  const firstName1 = primeros_nombres[Math.floor(Math.random() * primeros_nombres.length)];
  const firstName2 = primeros_nombres[Math.floor(Math.random() * primeros_nombres.length)];
  const lastName = apellidos[Math.floor(Math.random() * apellidos.length)];

  return Math.random() > 0.3
    ? `${firstName1} & ${firstName2} ${lastName}`
    : `Familia ${lastName}`;
}

/**
 * Genera una cantidad masiva de leads para testing de performance
 */
export function generateMassiveLeads(count: number = 500): Lead[] {
  const leads: Lead[] = [];

  for (let i = 0; i < count; i++) {
    // Datos básicos
    const scoring = {
      presupuesto: 60 + Math.random() * 40,
      zona: 50 + Math.random() * 50,
      datosCompletos: 40 + Math.random() * 60,
      timing: 30 + Math.random() * 70,
      credito: 50 + Math.random() * 50
    };

    // Score total
    const total = Math.round(
      (scoring.presupuesto * 0.25) +
      (scoring.zona * 0.20) +
      (scoring.datosCompletos * 0.20) +
      (scoring.timing * 0.20) +
      (scoring.credito * 0.15)
    );

    // Etapa basada en el score - leads con mejor score avanzan más
    let stage: number;
    if (total > 80) {
      // Excelente score: 70% en formalización (6-8), 30% en ventas (4-5)
      stage = Math.random() > 0.7 ? 4 + Math.floor(Math.random() * 2) : 6 + Math.floor(Math.random() * 3);
    } else if (total > 60) {
      // Buen score: 50% en ventas (4-5), 30% en negociación (3), 20% en calificación (1-2)
      const rand = Math.random();
      stage = rand > 0.5 ? 4 + Math.floor(Math.random() * 2) : rand > 0.2 ? 3 : 1 + Math.floor(Math.random() * 2);
    } else if (total > 40) {
      // Score medio: mayormente en calificación y negociación (1-3)
      stage = Math.random() > 0.3 ? 1 + Math.floor(Math.random() * 2) : 3;
    } else {
      // Score bajo: casi todos en calificación (1-2)
      stage = 1 + Math.floor(Math.random() * 2);
    }

    // Progreso coherente con el stage y score
    // Progreso alto para stages altos, pero con variabilidad
    let progress: number;
    if (stage >= 6) {
      progress = 60 + Math.floor(Math.random() * 40); // 60-99% para formalización
    } else if (stage >= 4) {
      progress = 40 + Math.floor(Math.random() * 50); // 40-89% para ventas
    } else if (stage >= 3) {
      progress = 30 + Math.floor(Math.random() * 50); // 30-79% para negociación
    } else {
      progress = 10 + Math.floor(Math.random() * 60); // 10-69% para calificación
    }

    // Valor de la unidad
    const baseValue = 250000000 + (Math.random() * 500000000);

    // Status coherente con score y stage
    let status: string;
    if (total >= 70 && stage >= 4) {
      status = 'Caliente';
    } else if (total >= 60 || stage >= 3) {
      status = Math.random() > 0.5 ? 'Caliente' : 'Tibio';
    } else if (total >= 40) {
      status = Math.random() > 0.7 ? 'Tibio' : 'Frío';
    } else {
      status = 'Frío';
    }

    const lead: Lead = {
      id: `lead-${i + 1}`,
      name: generateRandomName(i),
      stage,
      substage: Math.floor(Math.random() * 3) + 1,
      value: Math.round(baseValue),
      progress,
      status,

      perfil: {
        profesion: profesiones[Math.floor(Math.random() * profesiones.length)],
        ingresos: 5000000 + Math.random() * 20000000,
        miembros: 1 + Math.floor(Math.random() * 5),
        telefono: `+57 ${Math.floor(Math.random() * 900000000) + 100000000}`,
        email: `cliente${i + 1}@email.com`,
        motivacion: Math.random() > 0.5 ? 'Inversión' : Math.random() > 0.5 ? 'Vivienda propia' : 'Upgrade',
        canal: canales[Math.floor(Math.random() * canales.length)],
        fechaContacto: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toLocaleDateString()
      },

      unidadInteres: apartamentos[Math.floor(Math.random() * apartamentos.length)],

      preferencias: {
        torre: Math.random() > 0.5 ? 'Norte' : 'Sur',
        piso: `${5 + Math.floor(Math.random() * 15)}`,
        vista: Math.random() > 0.6 ? 'Ciudad' : Math.random() > 0.3 ? 'Montaña' : 'Parque',
        parqueaderos: 1 + Math.floor(Math.random() * 3),
        deposito: Math.random() > 0.3,
        financiacion: Math.random() > 0.4 ? 'Crédito bancario' : 'Recursos propios'
      },

      scoring: {
        ...scoring,
        total
      },

      color: total > 70 ? '#22C55E' : total > 50 ? '#F59E0B' : '#EF4444',

      // Campos de calificación - evaluación BANT basada en scoring
      qualification: {
        bant: {
          // Budget: basado en presupuesto y crédito
          budget: scoring.presupuesto >= 60 && scoring.credito >= 60,
          // Authority: probabilidad basada en datos completos
          authority: scoring.datosCompletos >= 70,
          // Need: basado en zona y timing
          need: scoring.zona >= 60,
          // Timeline: basado en timing
          timeline: scoring.timing >= 60
        },
        score: total,
        qualified: total >= 70,
        reason: total < 70
          ? (total < 40
            ? 'Score insuficiente para calificar'
            : 'Requiere más información para calificar')
          : undefined,
        processed: stage >= 2 // Procesado si ya avanzó de la etapa 1
      },

      // Campos de ventas - coherentes con score y stage
      sales: (() => {
        // Probabilidad de "No Interesado" basada en score y stage
        // Scores bajos y stages tempranos tienen más probabilidad
        const noInteresadoProbabilidad =
          total < 40 ? 0.25 : // 25% para score bajo
          total < 60 ? 0.15 : // 15% para score medio
          stage < 3 ? 0.10 : // 10% si aún está en calificación
          0.05; // 5% para scores altos en stages avanzados

        const noInteresado = Math.random() < noInteresadoProbabilidad;

        // Vistas de cotización coherentes con score, stage y resultado
        let cotizacionVistas = 0;

        if (!noInteresado && stage >= 4) {
          // Solo generan vistas si llegaron a ventas y no están como "No Interesado"
          if (total >= 75 && stage >= 5) {
            // Score alto y avanzado: mucho interés (5-12 vistas)
            cotizacionVistas = 5 + Math.floor(Math.random() * 8);
          } else if (total >= 60 && stage >= 4) {
            // Score medio en ventas: interés moderado (3-7 vistas)
            cotizacionVistas = 3 + Math.floor(Math.random() * 5);
          } else if (stage >= 4) {
            // Stage de ventas pero score bajo: bajo interés (1-4 vistas)
            cotizacionVistas = 1 + Math.floor(Math.random() * 4);
          }
        } else if (noInteresado && stage >= 4) {
          // Si están en "No Interesado" pero llegaron a ventas, tuvieron pocas vistas
          cotizacionVistas = Math.floor(Math.random() * 3); // 0-2 vistas
        }

        // Última visualización solo si tuvieron vistas
        const ultimaVisualizacion = cotizacionVistas > 0
          ? new Date(
              Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000
            ).toISOString()
          : undefined;

        return {
          cotizacionVistas,
          ultimaVisualizacion,
          noInteresado,
          motivoRechazo: noInteresado
            ? motivosRechazo[Math.floor(Math.random() * motivosRechazo.length)]
            : undefined
        };
      })(),

      // Campos de formalización (solo para leads en stage >= 5)
      formalization: stage >= 5 ? (() => {
        // Probabilidad de desistimiento basada en score y stage
        // Scores bajos tienen más probabilidad de desistir
        const desistimientoProbabilidad = total < 60 ? 0.15 : total < 75 ? 0.08 : 0.05;
        const desistido = Math.random() < desistimientoProbabilidad;

        // Si desistió, determinar en qué etapa lo hizo
        let desistimientoStage = stage;
        if (desistido) {
          // Puede desistir en cualquier etapa desde la 5 hasta su stage actual
          desistimientoStage = 5 + Math.floor(Math.random() * (stage - 4));
        }

        // Cuota inicial (30% del valor del inmueble)
        const initialPaymentDue = Math.round(baseValue * 0.30);

        // Estado de pago de cuota inicial basado en score y comportamiento
        let initialPaymentStatus: 'al-dia' | 'mora' | 'pendiente';
        let initialPaymentAmount = 0;

        if (stage >= 5 && !desistido) {
          // Clientes con buen score tienden a pagar completo y a tiempo
          if (total >= 75) {
            // 80% paga completo y a tiempo
            if (Math.random() > 0.2) {
              initialPaymentStatus = 'al-dia';
              initialPaymentAmount = initialPaymentDue;
            } else {
              // 20% paga parcial (en mora)
              initialPaymentStatus = 'mora';
              initialPaymentAmount = Math.round(initialPaymentDue * (0.4 + Math.random() * 0.4)); // 40-80% pagado
            }
          } else if (total >= 60) {
            // Score medio: 50% al día, 30% mora, 20% pendiente
            const rand = Math.random();
            if (rand > 0.5) {
              initialPaymentStatus = 'al-dia';
              initialPaymentAmount = initialPaymentDue;
            } else if (rand > 0.2) {
              initialPaymentStatus = 'mora';
              initialPaymentAmount = Math.round(initialPaymentDue * (0.3 + Math.random() * 0.5)); // 30-80% pagado
            } else {
              initialPaymentStatus = 'pendiente';
              initialPaymentAmount = 0;
            }
          } else {
            // Score bajo: 30% al día, 40% mora, 30% pendiente
            const rand = Math.random();
            if (rand > 0.7) {
              initialPaymentStatus = 'al-dia';
              initialPaymentAmount = initialPaymentDue;
            } else if (rand > 0.3) {
              initialPaymentStatus = 'mora';
              initialPaymentAmount = Math.round(initialPaymentDue * (0.2 + Math.random() * 0.6)); // 20-80% pagado
            } else {
              initialPaymentStatus = 'pendiente';
              initialPaymentAmount = 0;
            }
          }
        } else if (desistido && desistimientoStage === 5) {
          // Si desistió en cuota inicial, probablemente por problemas de pago
          initialPaymentStatus = Math.random() > 0.5 ? 'mora' : 'pendiente';
          initialPaymentAmount = initialPaymentStatus === 'mora'
            ? Math.round(initialPaymentDue * (0.1 + Math.random() * 0.3))
            : 0;
        } else {
          initialPaymentStatus = 'pendiente';
        }

        // Estado del crédito hipotecario
        let creditStatus: 'aprobado' | 'en-proceso' | 'rechazado' | 'pendiente' | undefined;
        let creditBank: string | undefined;

        if (stage >= 6 && !desistido) {
          // Probabilidad de aprobación basada en score y pago de cuota inicial
          const creditApprovalChance =
            total >= 75 && initialPaymentStatus === 'al-dia' ? 0.85 :
            total >= 60 && initialPaymentStatus !== 'pendiente' ? 0.65 :
            total >= 50 ? 0.40 : 0.20;

          const rand = Math.random();
          if (rand < creditApprovalChance) {
            creditStatus = 'aprobado';
            creditBank = bancos[Math.floor(Math.random() * bancos.length)];
          } else if (rand < creditApprovalChance + 0.15) {
            creditStatus = 'en-proceso';
          } else if (rand < creditApprovalChance + 0.25) {
            creditStatus = 'pendiente';
          } else {
            creditStatus = 'rechazado';
          }
        } else if (desistido && desistimientoStage === 6) {
          // Si desistió en crédito, probablemente fue rechazado
          creditStatus = Math.random() > 0.3 ? 'rechazado' : 'en-proceso';
        }

        // Escrituración
        let notaryDate: string | undefined;
        if (stage >= 7 && !desistido && creditStatus === 'aprobado') {
          // Solo programar escrituración si el crédito fue aprobado
          if (Math.random() > 0.3) {
            // Fecha en los últimos 90 días
            notaryDate = new Date(
              Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000
            ).toISOString();
          }
        }

        // Registro
        let registryDate: string | undefined;
        if (stage >= 8 && !desistido && notaryDate) {
          // Solo registrar si ya fue escriturado
          if (Math.random() > 0.2) {
            // Fecha posterior a la escrituración (15-60 días después)
            const notaryTime = new Date(notaryDate).getTime();
            registryDate = new Date(
              notaryTime + (15 + Math.random() * 45) * 24 * 60 * 60 * 1000
            ).toISOString();
          }
        }

        return {
          initialPayment: initialPaymentStatus === 'al-dia',
          initialPaymentStatus: stage >= 5 ? initialPaymentStatus : undefined,
          initialPaymentAmount: stage >= 5 ? initialPaymentAmount : undefined,
          initialPaymentDue: stage >= 5 ? initialPaymentDue : undefined,

          creditApproved: creditStatus === 'aprobado',
          creditStatus: stage >= 6 ? creditStatus : undefined,
          creditBank,

          notarized: stage >= 7 && !!notaryDate,
          notaryDate,

          registered: stage >= 8 && !!registryDate,
          registryDate,

          desistido,
          motivoDesistimiento: desistido
            ? motivosDesistimiento[Math.floor(Math.random() * motivosDesistimiento.length)]
            : undefined
        };
      })() : undefined
    };

    leads.push(lead);
  }

  return leads;
}

/**
 * Función para test de performance
 */
export function benchmarkLeadGeneration() {
  const leads = generateMassiveLeads(500);

  const leadsLarge = generateMassiveLeads(1000);

  return { leads500: leads, leads1000: leadsLarge };
}
